import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { createAppRouter } from './app/router.js';
import { AuthProvider, sessionQueryOptions } from './lib/auth.js';
import { setSessionExpiredHandler } from './lib/api-client.js';
import { ToastProvider } from './lib/toast.js';
import { useApplyBranding } from './features/branding/apply-branding.js';
import './styles/index.css';

// Dev-only visual-diff harness (design/orion-crm-design/CLAUDE_CODE_HANDOFF.md
// § Phase 0): `?__design=<Screen>` swaps every API call for fixture data and
// lets the REAL router/app render the REAL screen at its REAL URL — no
// separate preview route or component-wrapping needed, so route-bound hooks
// (`useParams`, `useSearch`, ...) work exactly as they do for a real
// visitor. `import.meta.env.DEV` makes the whole branch, and everything it
// imports, dead-code-eliminated out of a production build.
let designScreen: string | null = null;
if (import.meta.env.DEV) {
  designScreen = new URLSearchParams(window.location.search).get('__design');
  if (designScreen) {
    const { SCREEN_REGISTRY, allFixturesFor } = await import('./design-preview/registry.js');
    const { installFixtureFetch } = await import('./design-fixtures/fixture-fetch.js');
    const entry = SCREEN_REGISTRY[designScreen];
    if (entry) installFixtureFetch(allFixturesFor(entry));
    else console.error(`No design-preview fixtures registered for "${designScreen}".`);
  }
}

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: designScreen ? false : 1, refetchOnWindowFocus: false } },
});
const router = createAppRouter(queryClient);

// A 401 from any authenticated endpoint — most often the session having
// idle-timed-out while the tab sat open — otherwise just left whatever
// query failed to show its own inline error (docs: "Couldn't load the
// dashboard"), stranding the user on a dead page instead of sending them
// back to sign in. Checked against the router's own current location
// rather than a sticky "already redirecting" flag: several queries on one
// page can all 401 at once (e.g. the dashboard's parallel widgets), and a
// flag that's never cleared would stop this from ever firing again after
// the first session expiry of the whole tab's lifetime.
setSessionExpiredHandler(() => {
  queryClient.setQueryData(sessionQueryOptions.queryKey, undefined);
  if (router.state.location.pathname === '/login') return;
  void router.navigate({ to: '/login' });
});

function App() {
  useApplyBranding();
  return (
    <ToastProvider>
      <AuthProvider>
        <RouterProvider router={router} />
      </AuthProvider>
    </ToastProvider>
  );
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </StrictMode>,
);
